import React from 'react';
import { CustomizedButton, CustomizedDialog } from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import { CreateOutlined } from '@material-ui/icons';
import { Box, makeStyles, TextField } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import OnProcessingWarningModal from './OnProcessingWarningModal';

import {
  CapaErrorCode,
  useRequestAdjustRfqQuotationMutation
} from '@generated/graphql';

interface Props {
  open: boolean;
  rfqId?: string;
  partnerId?: string;
  matches: boolean;
  onClose: () => void;
  hasProgressingPayment?: boolean | null;
}

const QuotationRequestForFixModal = ({
  open,
  rfqId,
  partnerId,
  matches,
  onClose,
  hasProgressingPayment = false
}: Props) => {
  const classes = useStyles();

  const { control, handleSubmit, errors } = useForm<{ edit: string }>();

  const { enqueueSnackbar } = useSnackbar();

  // 수정요청
  const [adjustTrigger] = useRequestAdjustRfqQuotationMutation();

  const onSubmit = (form: { edit: string }) => {
    adjustTrigger({
      variables: {
        input: {
          rfqId,
          quotationPartnerId: partnerId,
          adjustRequest: form.edit
        }
      }
    }).then((res) => {
      if (
        res.data?.requestAdjustRfqQuotation.result === CapaErrorCode.Success
      ) {
        enqueueSnackbar('파트너에게 견적서 수정 요청이 전달되었습니다.', {
          variant: 'success',
          anchorOrigin: {
            horizontal: matches ? 'center' : 'left',
            vertical: 'bottom'
          }
        });
      }
    });

    onClose();
  };

  if (hasProgressingPayment) {
    return <OnProcessingWarningModal open={true} onClose={onClose} />;
  }

  return (
    <CustomizedDialog
      color="primary"
      icon={<CreateOutlined />}
      title="견적서 수정 요청"
      open={open}
      onClose={onClose}
      maxWidth={476}
    >
      <Box width="100%">
        <Controller
          defaultValue=""
          control={control}
          rules={{
            maxLength: 200,
            required: true
          }}
          name="edit"
          render={(renderProps) => (
            <TextField
              {...renderProps}
              error={errors['edit'] ? true : false}
              helperText={errors['edit'] && '수정 요청 정보를 입력해주세요.'}
              className={classes.textField}
              maxRows={5}
              minRows={5}
              multiline
              variant="outlined"
              fullWidth
              placeholder="파트너에게 수정을 요청할 정보를 입력해주세요. (최대 200자 입력)"
              onChange={(e) => {
                if (e.currentTarget?.value.length > 200) return '';
                return renderProps.onChange(e);
              }}
            />
          )}
        />
      </Box>

      <Box marginTop={4} width="100%" className={classes.actionCTA}>
        <CustomizedButton fullWidth color="secondary" onClick={onClose}>
          취소
        </CustomizedButton>

        <CustomizedButton
          fullWidth
          color="primary"
          onClick={handleSubmit(onSubmit)}
        >
          파트너에게 보내기
        </CustomizedButton>
      </Box>
    </CustomizedDialog>
  );
};

export default QuotationRequestForFixModal;

const useStyles = makeStyles((theme) => ({
  textField: {
    '& .MuiOutlinedInput-multiline': {
      padding: `8px 12px`,
      minHeight: 116
    },
    '& textarea': {
      fontSize: 14,
      '&::placeholder': {
        fontSize: 14
      }
    },
    '& .MuiFormHelperText-contained': {
      '&.Mui-error': {
        fontSize: 12,
        fontWeight: 400,
        marginLeft: 0,
        marginTop: 4
      }
    }
  },
  actionCTA: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 32,
    flexWrap: 'nowrap',
    '& > :first-child': {
      marginRight: 8,
      color: colorSet.gray900
    }
  }
}));
